import React, { useState } from "react";
import { Modal, Dropdown, IDropdownOption, TextField, PrimaryButton, DefaultButton, Checkbox } from "@fluentui/react";
import { ChatAppResponse, FeedbackRequest, submitFeedbackApi } from "../../api";
import { useGetAppServicesToken, useGetToken } from "../../authConfig";
import styles from "./FeedbackModal.module.css";

const CATEGORIES: IDropdownOption[] = [
    { key: "general feedback", text: "General Feedback" },
    { key: "bug", text: "Bug" },
    { key: "feature request", text: "Feature Request" },
    { key: "wrong answer", text: "Wrong Answer" },
    { key: "other", text: "Other" }
];

interface Props {
    isOpen: boolean;
    onClose: () => void;
    answers: [string, ChatAppResponse][];
    conversationId: string | null;
    powerplant: string;
}

const FeedbackModal: React.FC<Props> = ({ isOpen, onClose, answers, conversationId, powerplant }) => {
    const [category, setCategory] = useState("general feedback");
    const [comments, setComments] = useState("");
    const [selected, setSelected] = useState<Set<number>>(new Set());
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [done, setDone] = useState(false);

    const token = useGetToken();
    const { user_claims } = useGetAppServicesToken();
    const userId = (user_claims?.preferred_username as string) ?? (user_claims?.email as string) ?? "unknown";
    const recent = answers.slice(-10);
    const isWrong = category === "wrong answer";
    const canSubmit = comments.length >= 5 && (!isWrong || selected.size > 0);

    const reset = () => { setCategory("general feedback"); setComments(""); setSelected(new Set()); setError(null); setDone(false); };
    const close = () => { if (!submitting) { onClose(); setTimeout(reset, 300); } };
    const toggle = (i: number) => { const s = new Set(selected); s.has(i) ? s.delete(i) : s.add(i); setSelected(s); };

    const submit = async () => {
        if (!canSubmit || submitting) return;
        setSubmitting(true); setError(null);
        try {
            await submitFeedbackApi({
                category, user_id: userId,
                conversation_id: conversationId ?? undefined,
                comments,
                wrong_answers: isWrong
                    ? Array.from(selected).map(i => ({ user: recent[i][0], agent: recent[i][1].choices[0].message.content }))
                    : null
            } as FeedbackRequest, token, powerplant);
            setDone(true);
            setTimeout(() => { onClose(); setTimeout(reset, 300); }, 1800);
        } catch (e: any) {
            setError(e?.message ?? "Failed to submit. Please try again.");
        } finally { setSubmitting(false); }
    };

    return (
        <Modal isOpen={isOpen} onDismiss={close} isBlocking={false}>
            <div className={styles.container}>
                <button onClick={close} className={styles.closeButton}>×</button>
                <h2 className={styles.title}>Provide your feedback</h2>
                <div className={styles.titleUnderline} />
                <p className={styles.description}>
                    Please share any feedback about the application. This can include reporting bugs, suggesting improvements, or proposing new ideas.
                </p>
                {done ? (
                    <div className={styles.successMessage}>Thank you! Your feedback has been submitted.</div>
                ) : <>
                    <div className={styles.field}>
                        <label className={styles.label}><span className={styles.required}>* </span>Category</label>
                        <Dropdown options={CATEGORIES} selectedKey={category} onChange={(_, o) => o && setCategory(o.key as string)} />
                    </div>
                    {isWrong && (
                        <div className={styles.field}>
                            <label className={styles.label}><span className={styles.required}>* </span>Select failed answer(s)</label>
                            <div className={styles.qaList}>
                                {recent.length === 0
                                    ? <p className={styles.emptyQA}>No conversation history.</p>
                                    : recent.map((pair, i) => (
                                        <div key={i} className={styles.qaPair}>
                                            <div className={styles.question}>
                                                <b className={styles.qLabel}>You</b><span>{pair[0]}</span>
                                            </div>
                                            <Checkbox
                                                label={pair[1].choices[0].message.content.slice(0, 200) + (pair[1].choices[0].message.content.length > 200 ? "\u2026" : "")}
                                                checked={selected.has(i)} onChange={() => toggle(i)}
                                                styles={{ label: { fontSize: 12, fontFamily: "monospace" }, checkbox: { borderRadius: "50%" } }}
                                            />
                                        </div>
                                    ))}
                            </div>
                        </div>
                    )}
                    <TextField
                        label="Your feedback" required multiline rows={4} maxLength={500} value={comments}
                        onChange={(_, v) => setComments(v ?? "")}
                        description={`${comments.length} / 500`}
                        styles={{ root: { marginBottom: 12 }, field: { fontFamily: "monospace" } }}
                    />
                    {error && <div className={styles.errorMessage}>{error}</div>}
                    <div className={styles.actions}>
                        <DefaultButton onClick={close} disabled={submitting} text="Cancel" />
                        <PrimaryButton onClick={submit} disabled={!canSubmit || submitting} text={submitting ? "Sending\u2026" : "Send feedback"} />
                    </div>
                </>}
            </div>
        </Modal>
    );
};

export default FeedbackModal;

